import React, { useState } from 'react'
import { FcGoogle } from 'react-icons/fc'
import Image from 'cwp-components/placeholders/Image'
import StarRating from './StarRating'
import moment from 'moment'
import { SiTrustpilot } from 'react-icons/si'
import { ReportReviewWizard } from './ReportReview/ReportReviewWizard/ReportReviewWizard'

interface ReviewResponse {
  avatar: string
  name: string
  lawyer?: string
  lawFirm?: string
  message: string
  id: string
}

export interface ReviewData {
  avatar: string
  name: string
  date?: string
  rating?: number
  id: string
  message: string
  link?: string
  logo?: string
  responses?: ReviewResponse[]
  source: string
  review_type: string
}

interface ProfileReviewProps {
  reviewData: ReviewData
}

export function ProfileReview({ reviewData }: ProfileReviewProps) {
  const [responseDropdownOpen, setResponseDropdownOpen] = useState('')
  const [isReportReviewWizardOpen, setIsReportReviewWizardOpen] =
    useState(false)

  const getReviewLink = (reviewType: string): string => {
    switch (reviewType) {
      case 'Legal500':
        return 'https://www.legal500.com/how-it-works/'
      case 'Feefo':
        return 'https://business.feefo.com/support/content-moderation'
      case 'ReviewSolicitors':
        return 'https://www.reviewsolicitors.co.uk/about-reviews'
      case 'Chambers&Partners':
        return 'https://chambers.com/faqs/how-is-referee-feedback-collected-18-7-2022-34040000000093039:34040000000314113'
      default:
        return ''
    }
  }

  return (
    <div
      key={reviewData.id}
      className='review pl-2 pr-12 border-l-[3px] border-[rgba(217,224,223,0.50)] mb-4'
    >
      <div className='flex justify-between items-center self-stretch flex-wrap'>
        <div>
          <div className='flex items-center gap-[4px] md:gap-[8px] flex-wrap'>
            {reviewData?.avatar ? (
              <img
                src={reviewData.avatar}
                alt='avatar'
                className='w-8 h-8 rounded-full'
              />
            ) : (
              <Image
                height='32px'
                width='32px'
                background='#101828'
                className='flex items-center justify-center !rounded-3xl'
              />
            )}
            <span className='text-[#37455C] font-medium text-base leading-6 tracking-wide'>
              {!!reviewData?.name?.trim() ? reviewData.name : 'Anonymous'}
            </span>
            <span className='text-[#37455C] font-normal text-sm leading-5 tracking-wide pl-2 border-l-[2px] border-[#D8D8DF]'>
              {moment(reviewData['review_date']).format('MMMM YYYY')}
            </span>
          </div>
          <div className='flex pl-10 justify-start self-stretch flex-wrap pt-1'>
            <span className='text-[#A0ABBE] font-medium text-xs'>
              ID: {reviewData.id}
            </span>
          </div>
        </div>
        <div className='flex flex-col items-end ml-auto text-right'>
          <div className='flex min-h-6 h-auto items-center gap-[5px] flex-wrap pt-3 md:pt-0 lg:pt-3'>
            {reviewData.review_type === 'lu' && (
              <img
                src={'/LU-Logo.png'}
                className='h-[21px] w-auto'
                alt='Logo'
              />
            )}
            {reviewData.review_type === 'feefo' && (
              <img
                src={'/Feefo-Logo.png'}
                className='h-auto w-[21px] bg-[#4D4D4F] p-1 rounded'
                alt='Logo'
              />
            )}
            {reviewData.review_type === 'chambers&partners' && (
              <img
                src={'/chambers-and-partners.svg'}
                className='h-[21px] w-auto'
                alt='Logo'
              />
            )}
            {reviewData.review_type === 'reviewsolicitors' && (
              <img
                src={'/reviewsolicitors.png'}
                className='w-[21px] h-auto'
                alt='Logo'
              />
            )}
            {reviewData.review_type === 'legal500' && (
              <img
                src={'/legal500.svg'}
                className='w-[21px] h-auto'
                alt='Logo'
              />
            )}
            {reviewData['review_type'] === 'google' && (
              <FcGoogle fontSize={21} />
            )}
            {reviewData['review_type'] === 'trustpilot' && (
              <SiTrustpilot fontSize={21} className='text-[#219653] ' />
            )}
            <StarRating rating={reviewData['review_rating']} />
            <span className='text-black text-[14px] font-medium leading-normal'>
              {reviewData['review_rating']}
            </span>
          </div>
          <span className='text-[#001A3D] text-right font-medium text-sm'>
            {reviewData?.review_type !== 'lu' ? (
              <a
                className='underline'
                href={reviewData.link || getReviewLink(reviewData.review_type)}
                target='_blank'
                rel='noopener noreferrer'
              >
                Check verification process
              </a>
            ) : (
              'Verified by Legal Utopia'
            )}
          </span>
        </div>
      </div>
      <p className='text-[#25395C] text-justify font-normal text-base leading-5 tracking-wide my-2'>
        {reviewData?.message}
      </p>
      <div className='flex flex-col pl-8 items-start gap-[10px] self-stretch'>
        {reviewData?.responses.length > 0 &&
          reviewData.responses.map((response) => (
            <>
              <div
                key={response.id}
                className='flex items-center gap-2 self-stretch cursor-pointer'
                onClick={() =>
                  setResponseDropdownOpen(
                    responseDropdownOpen === response?.id ? '' : response?.id,
                  )
                }
              >
                {response.avatar ? (
                  <img
                    src={response?.avatar}
                    alt='avatar'
                    className='w-8 h-8 rounded-full'
                  />
                ) : (
                  <Image
                    height='32px'
                    width='32px'
                    background='#101828'
                    className='flex items-center justify-center !rounded-3xl'
                  />
                )}
                <span className='text-[#37455C] font-medium text-base leading-6 tracking-wide'>
                  {response?.name}
                </span>
                {responseDropdownOpen === response.id ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='12'
                    height='12'
                    viewBox='0 0 12 12'
                    fill='none'
                  >
                    <path d='M6 3L12 9L0 9L6 3Z' fill='#37A2BA' />
                  </svg>
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='12'
                    height='12'
                    viewBox='0 0 12 12'
                    fill='none'
                  >
                    <path d='M6 9L0 3H12L6 9Z' fill='#37A2BA' />
                  </svg>
                )}
              </div>
              {responseDropdownOpen === response.id && (
                <div className='flex flex-col p-5 items-start gap-2 self-stretch rounded-lg bg-[#F4F4F4]'>
                  <p className='text-black font-medium text-xs'>
                    Response from:
                    <span className='text-black font-medium text-xs'>
                      {response?.name}
                    </span>
                    {response?.lawFirm ? 'at' : ''}
                    <span className='text-black font-medium text-xs'>
                      {' '}
                      {response?.lawFirm}
                    </span>
                  </p>
                  <p className='text-black text-justify font-normal text-base leading-5 tracking-wide'>
                    {response?.message}
                  </p>
                  <div className='flex justify-end w-full'>
                    <p className='text-[#A0ABBE] font-medium text-xs text-right'>
                      ID: {response?.id}
                    </p>
                  </div>
                </div>
              )}
            </>
          ))}
        <div className='flex justify-end w-full'>
          <button
            onClick={() => setIsReportReviewWizardOpen(true)}
            className='bg-transparent border-none p-0 m-0 appearance-none text-[#001A3D] text-center font-medium text-sm underline'
          >
            Report
          </button>
          <ReportReviewWizard
            reviewId={reviewData.id}
            reviewType={reviewData.review_type}
            isOpen={isReportReviewWizardOpen}
            handleClose={() => setIsReportReviewWizardOpen(false)}
          />
        </div>
      </div>
    </div>
  )
}
